import { ChangeEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LoginState, EnumQuizType, RoutePath } from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { useAuth } from '@quizzach/contexts';

export const useLogin = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { setToken } = useAuth();
	const [errorCode, setErrorCode] = useState('');

	/**
	 * Redirects the user based on the source of the login attempt.
	 */
	const handleRedirect = () => {
		if (!location.state) {
			navigate('/profile');
		} else if (location.state.source) {
			switch (location.state.source) {
				case 'quiz':
					navigate(RoutePath.QuizStart);
					break;
				case 'generator':
					navigate(RoutePath.GeneratorCustomizer, {
						state: { source: EnumQuizType.generator },
					});
					break;
				case 'quick':
				case 'quick-init':
					navigate(RoutePath.QuickPick);
					break;
				case 'profile':
					navigate(RoutePath.Profile);
					break;
			}
		} else if (location.state.redirect) {
			navigate(location.state.redirect);
		}
	};

	const login = async (
		state: LoginState,
		setError: (error: boolean) => void
	) => {
		window.localStorage.removeItem('userToken');
		sessionStorage.removeItem('referer');

		try {
			const response = await fetchData<{ token: string }>(`/api/login`, {
				method: 'POST',
				body: JSON.stringify(state),
			});

			if (response.status >= 300) {
				consoleDev('login', ConsoleStatus.HTTP, response.status);

				if (response.status === 401) {
					setErrorCode('401');
				}
				setError(true);
			} else {
				consoleDev('login', ConsoleStatus.SUCC);
				if (response.data) {
					setToken(response.data.token);
					return handleRedirect();
				}
			}
		} catch (err) {
			consoleDev('login', ConsoleStatus.NET, err);

			setError(true);
		}
	};

	const onSubmit = (
		e: React.FormEvent<HTMLFormElement>,
		state: LoginState,
		setError: (error: boolean) => void
	) => {
		e.preventDefault();

		if (state.email.length != 0 && state.password.length != 0)
			login(state, setError);
		else setError(true);
	};

	const handleInputChange = (
		e: ChangeEvent<HTMLInputElement>,
		setState: React.Dispatch<React.SetStateAction<LoginState>>
	) => {
		const { name, value } = e.target;
		setState((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const togglePasswordVisibility = (
		showPassword: boolean,
		setShowPassword: (show: boolean) => void
	) => {
		setShowPassword(!showPassword);
	};

	return {
		errorCode,
		onSubmit,
		handleInputChange,
		togglePasswordVisibility,
	};
};
